import React from "react";
import { Activity, Droplets, Wheat, Droplet } from "lucide-react";
import { Toggle } from "../ui/toggle";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Section, FormField } from "./InspectionShared";

const FrameByFrameSection = ({ formData, setFormData, toggleArray, handleSectionPhoto, isQuickInspection, hive, DynamicFrameSelectors }) => {
  return (
        <Section 
          title="Frame-by-Frame Observations" 
          icon={<Activity className="h-6 w-6 text-amber-600" />}
          collapsible={isQuickInspection}
          defaultCollapsed={isQuickInspection}
          onPhoto={handleSectionPhoto}
        >
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
            <div className="font-medium text-stone-800 mb-3 text-sm">👆 Legend - Tap each frame to cycle through options:</div>
            <div className="flex flex-wrap gap-2 text-xs">
              <div className="flex items-center gap-2 px-3 py-2 rounded bg-white border min-w-fit">
                <span className="w-6 h-6 rounded bg-white border-2 border-stone-300 flex items-center justify-center font-bold shrink-0">-</span>
                <span className="text-stone-600 whitespace-nowrap">Empty</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-2 rounded bg-white border min-w-fit">
                <span className="w-6 h-6 rounded bg-stone-200 border-2 border-stone-400 flex items-center justify-center font-bold text-stone-700 shrink-0">F</span>
                <span className="text-stone-600 whitespace-nowrap">Found.</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-2 rounded bg-white border min-w-fit">
                <span className="w-6 h-6 rounded bg-yellow-100 border-2 border-yellow-400 flex items-center justify-center font-bold text-yellow-800 shrink-0">C</span>
                <span className="text-stone-600 whitespace-nowrap">Comb</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-2 rounded bg-white border min-w-fit">
                <span className="w-6 h-6 rounded bg-amber-200 border-2 border-amber-500 flex items-center justify-center font-bold text-amber-800 shrink-0">B</span>
                <span className="text-stone-600 whitespace-nowrap">Brood</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-2 rounded bg-white border min-w-fit">
                <span className="w-6 h-6 rounded bg-blue-200 border-2 border-blue-400 flex items-center justify-center font-bold text-blue-800 shrink-0">D</span>
                <span className="text-stone-600 whitespace-nowrap">Drone</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-2 rounded bg-white border min-w-fit">
                <span className="w-6 h-6 rounded bg-orange-200 border-2 border-orange-400 flex items-center justify-center font-bold text-orange-800 shrink-0">P</span>
                <span className="text-stone-600 whitespace-nowrap">Pollen</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-2 rounded bg-white border min-w-fit">
                <span className="w-6 h-6 rounded bg-green-100 border-2 border-green-400 flex items-center justify-center font-bold text-green-800 shrink-0">N</span>
                <span className="text-stone-600 whitespace-nowrap">Nectar</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-2 rounded bg-white border min-w-fit">
                <span className="w-6 h-6 rounded bg-amber-300 border-2 border-amber-600 flex items-center justify-center font-bold text-amber-900 shrink-0">H</span>
                <span className="text-stone-600 whitespace-nowrap">Honey</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-2 rounded bg-white border min-w-fit">
                <span className="w-6 h-6 rounded bg-yellow-300 border-2 border-yellow-600 flex items-center justify-center font-bold text-yellow-900 text-[10px] shrink-0">BB</span>
                <span className="text-stone-600 whitespace-nowrap">Bee Bread</span>
              </div>
            </div>
          </div>

          {/* Dynamic Frame Selectors based on Hive Configuration */}
          <DynamicFrameSelectors 
            hive={hive}
            formData={formData}
            setFormData={setFormData}
          />

          <FormField label="Brood">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {['None', 'Eggs', 'Larvae', 'Capped', 'Drone', 'BIAS'].map((brood) => (
                <Toggle
                  key={brood}
                  pressed={formData.brood.includes(brood)}
                  onPressedChange={() => toggleArray('brood', brood)}
                  data-testid={`brood-${brood.toLowerCase()}`}
                  className="h-16 data-[state=on]:bg-lime-600 data-[state=on]:text-white"
                >
                  {brood}
                </Toggle>
              ))}
            </div>
          </FormField>

          <FormField label="Pattern (Select all that apply)">
            <div className="grid grid-cols-3 gap-2">
              {['Solid', 'Partly Spotty', 'Spotty'].map((pattern) => (
                <Toggle
                  key={pattern}
                  pressed={formData.pattern.includes(pattern)}
                  onPressedChange={() => toggleArray('pattern', pattern)}
                  data-testid={`pattern-${pattern.toLowerCase().replace(/\s+/g, '-')}`}
                  className="h-16 data-[state=on]:bg-lime-600 data-[state=on]:text-white"
                >
                  {pattern}
                </Toggle>
              ))}
            </div>
          </FormField>

          <FormField label="Queen Status">
            <div className="flex gap-2">
              <Toggle
                pressed={formData.queen_spotted === true}
                onPressedChange={() => setFormData({ ...formData, queen_spotted: true, no_queen: false })}
                data-testid="queen-spotted-yes"
                className="h-16 flex-1 data-[state=on]:bg-lime-600 data-[state=on]:text-white font-bold"
              >
                Queen Spotted
              </Toggle>
              <Toggle
                pressed={formData.queen_spotted === false && !formData.no_queen}
                onPressedChange={() => setFormData({ ...formData, queen_spotted: false, no_queen: false })}
                data-testid="queen-spotted-no"
                className="h-16 flex-1 data-[state=on]:bg-stone-400 data-[state=on]:text-white font-bold"
              >
                Not Seen
              </Toggle>
              <Toggle
                pressed={formData.no_queen === true}
                onPressedChange={() => setFormData({ ...formData, queen_spotted: false, no_queen: true })}
                data-testid="no-queen"
                className="h-16 flex-1 data-[state=on]:bg-red-600 data-[state=on]:text-white font-bold"
              >
                ⚠️ No Queen
              </Toggle>
            </div>
          </FormField>

          {formData.queen_spotted && (
            <div className="space-y-6 bg-lime-50 border border-lime-200 rounded-xl p-6">
              <FormField label="Queen on Frame #">
                <div className="grid grid-cols-5 md:grid-cols-10 gap-2">
                  {Array.from({ length: 10 }, (_, i) => (
                    <Toggle
                      key={i + 1}
                      pressed={formData.queen_frame === String(i + 1)}
                      onPressedChange={() => setFormData({ ...formData, queen_frame: String(i + 1) })}
                      data-testid={`queen-frame-${i + 1}`}
                      className="h-16 data-[state=on]:bg-lime-600 data-[state=on]:text-white"
                    >
                      {i + 1}
                    </Toggle>
                  ))}
                </div>
              </FormField>

              <FormField label="Queen Marked">
                <Toggle
                  pressed={formData.queen_marked}
                  onPressedChange={(pressed) => setFormData({ ...formData, queen_marked: pressed })}
                  data-testid="queen-marked-toggle"
                  className="h-16 w-full data-[state=on]:bg-lime-600 data-[state=on]:text-white"
                >
                  {formData.queen_marked ? '✓ Marked' : 'Not Marked'}
                </Toggle>
              </FormField>

              {formData.queen_marked && (
                <FormField label="Queen Color">
                  <div className="grid grid-cols-5 gap-2">
                    {[
                      { name: 'Red', bg: 'bg-red-500', ring: 'ring-red-700' },
                      { name: 'Blue', bg: 'bg-blue-500', ring: 'ring-blue-700' },
                      { name: 'Green', bg: 'bg-green-500', ring: 'ring-green-700' },
                      { name: 'Yellow', bg: 'bg-yellow-400', ring: 'ring-yellow-600' },
                      { name: 'White', bg: 'bg-white', ring: 'ring-stone-400' }
                    ].map(({ name, bg, ring }) => (
                      <button
                        key={name}
                        type="button"
                        onClick={() => setFormData({ ...formData, queen_color: name })}
                        data-testid={`queen-color-${name.toLowerCase()}`}
                        className={`h-20 rounded-xl transition-all ${bg} ${
                          formData.queen_color === name ? `ring-4 ${ring} scale-105` : 'border-2 border-stone-300'
                        } ${name === 'White' ? 'text-stone-800' : 'text-white'} font-bold text-sm flex flex-col items-center justify-center gap-1 hover:scale-105`}
                      >
                        <div className={`w-8 h-8 rounded-full ${bg} ${name === 'White' ? 'border-2 border-stone-400' : ''}`}></div>
                        <span>{name}</span>
                      </button>
                    ))}
                  </div>
                </FormField>
              )}

              <FormField label="Queen Age (Years)">
                <div className="grid grid-cols-5 gap-2">
                  {['1', '2', '3', '4', '5'].map((age) => (
                    <Toggle
                      key={age}
                      pressed={formData.queen_age === age}
                      onPressedChange={() => setFormData({ ...formData, queen_age: age })}
                      data-testid={`queen-age-${age}`}
                      className="h-16 data-[state=on]:bg-lime-600 data-[state=on]:text-white"
                    >
                      {age}
                    </Toggle>
                  ))}
                </div>
              </FormField>
            </div>
          )}

          <FormField label="Queen Cells">
            <div className="flex gap-2">
              <Toggle
                pressed={formData.queen_cells === true}
                onPressedChange={() => setFormData({ ...formData, queen_cells: true })}
                data-testid="queen-cells-yes"
                className="h-16 flex-1 data-[state=on]:bg-amber-600 data-[state=on]:text-white font-bold"
              >
                YES
              </Toggle>
              <Toggle
                pressed={formData.queen_cells === false}
                onPressedChange={() => setFormData({ ...formData, queen_cells: false })}
                data-testid="queen-cells-no"
                className="h-16 flex-1 data-[state=on]:bg-stone-400 data-[state=on]:text-white font-bold"
              >
                NO
              </Toggle>
            </div>
          </FormField>

          {formData.queen_cells && (
            <div className="space-y-6 bg-amber-50 border border-amber-200 rounded-xl p-6">
              <FormField label="Queen Cells on Frame # (Select all that apply)">
                <div className="grid grid-cols-5 md:grid-cols-10 gap-2">
                  {Array.from({ length: 10 }, (_, i) => (
                    <Toggle
                      key={i + 1}
                      pressed={formData.queen_cells_frames.includes(i + 1)}
                      onPressedChange={() => toggleArray('queen_cells_frames', i + 1)}
                      data-testid={`queen-cells-frame-${i + 1}`}
                      className="h-16 data-[state=on]:bg-amber-600 data-[state=on]:text-white"
                    >
                      {i + 1}
                    </Toggle>
                  ))}
                </div>
              </FormField>

              <FormField label="Capped Cells">
                <Toggle
                  pressed={formData.capped_cells}
                  onPressedChange={(pressed) => setFormData({ ...formData, capped_cells: pressed })}
                  data-testid="capped-cells-toggle"
                  className="h-16 w-full data-[state=on]:bg-amber-600 data-[state=on]:text-white"
                >
                  {formData.capped_cells ? '✓ Capped Cells Present' : 'No Capped Cells'}
                </Toggle>
              </FormField>

              {formData.capped_cells && (
                <FormField label="Capped Cells on Frame # (Select all that apply)">
                  <div className="grid grid-cols-5 md:grid-cols-10 gap-2">
                    {Array.from({ length: 10 }, (_, i) => (
                      <Toggle
                        key={i + 1}
                        pressed={formData.capped_cells_frames.includes(i + 1)}
                        onPressedChange={() => toggleArray('capped_cells_frames', i + 1)}
                        data-testid={`capped-cells-frame-${i + 1}`}
                        className="h-16 data-[state=on]:bg-amber-600 data-[state=on]:text-white"
                      >
                        {i + 1}
                      </Toggle>
                    ))}
                  </div>
                </FormField>
              )}
            </div>
          )}

          <FormField label="Drone Population">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {['None', 'Low', 'Average', 'High', 'Worker Layer', 'Old Queen'].map((pop) => (
                <Toggle
                  key={pop}
                  pressed={formData.drone_population === pop}
                  onPressedChange={() => setFormData({ ...formData, drone_population: pop })}
                  data-testid={`drone-population-${pop.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`h-16 ${
                    ['Worker Layer', 'Old Queen'].includes(pop)
                      ? 'data-[state=on]:bg-red-600'
                      : 'data-[state=on]:bg-lime-600'
                  } data-[state=on]:text-white`}
                >
                  {pop}
                </Toggle>
              ))}
            </div>
          </FormField>

          <FormField label="Swarm Conditions">
            <Toggle
              pressed={formData.swarm_conditions}
              onPressedChange={(pressed) => setFormData({ ...formData, swarm_conditions: pressed })}
              data-testid="swarm-conditions-toggle"
              className="h-16 w-full data-[state=on]:bg-red-600 data-[state=on]:text-white font-bold text-lg"
            >
              {formData.swarm_conditions ? '⚠ Swarm Conditions Present' : 'No Swarm Conditions'}
            </Toggle>
          </FormField>
        </Section>
  );
};

export default FrameByFrameSection;
